import React, {
    useCallback,
    useEffect,
    useLayoutEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import {
    updateImportedProduct,
    getKDMaxProduct,
} from 'service/KDMaxImportService';
import {
    genericMessageHandler,
    getDoorInfo,
    getPanelValidation,
    useComponentMountedHelper,
    useQueryParams,
    getOptions,
    getCabinetFields,
} from 'shared/helpers';
import {useAppContext} from 'contexts/ApplicationContext';
import {useJobContext} from 'contexts/JobContext';
import {useNotificationContext} from 'contexts/NotificationContext';
import {useProductContext} from 'contexts/ProductContext';
import {validateProducts, checkMaterialRestrictions} from 'shared/validator';
import {RECENTLY_ADDED_KEYS} from 'hooks/RecentlyAdded';
import {useParams} from 'react-router-dom';
import {useNavigate} from 'shared/reload/helper/useNavigate';
import {getRequiredReferences} from 'components/customer/Product/helpers/getRequiredReferences';
import {getInitialValues} from 'components/customer/Product/helpers/getInitialValues';
import {mapInitialValues} from 'components/customer/Product/helpers/mapInitialValues';
import {
    mapDefaults,
    applyAdvancedDoorDrawerFaceTypeDefault,
} from 'components/customer/Product/helpers/mapDefaults';
import {getFormSchema} from 'components/customer/Product/helpers/getFormSchema';
import {getTabErrors} from 'components/customer/Product/helpers/getTabErrors';
import {mapProductData} from 'components/customer/Product/helpers/mapProductData';
import {useMaterialValues} from 'components/customer/Product/helpers/useMaterialValues';
import {parseHtmlString} from 'shared/helpers/HTMLParser';
import {
    invalidateParticularRoomAndCost,
    useGetRoomProductsQuery,
    useLazyGetQFPProductStructureQuery,
    useSaveProductMutation,
    invalidateJob,
    useLazyGetCabinetDetailsQuery,
} from 'components/customer/Product/store/productApi';
import {useLazyGetProductDataQuery} from 'components/customer/Product/store/useProductApi';
import {cloneDeep} from 'lodash';
import {getMaterialRestrictionDetails} from 'components/customer/Product/helpers/getMaterialRestrictionDetails';
import {checkCabinetTopRestriction} from 'components/customer/Product/helpers/checkCabinetTopRestriction';
import {getFaceHeight} from 'components/customer/Product/Drawer/helpers';
import {getMaterial} from 'components/customer/Materials/store/selectors/materialSelector';
import {store, useAppDispatch, useAppSelector} from 'store/customer';
import {MaterialType} from 'components/customer/Materials/entity';
import {useGetProductDefaultQuery} from 'components/customer/Product/helpers/useGetProductDefaultQuery';
import {useChangedData} from 'components/customer/Product/helpers/useChangedData';
import {getEdge} from 'components/customer/Materials/store/selectors/edgeSelector';
import {getDoor} from 'components/customer/Materials/store/selectors/doorSelector';
import {
    selectFaceHeightErrors,
    selectRunnersLoadingState,
} from 'components/customer/Product/store/drawerRunnerSlice';
import {invalidateTotalProductCount} from 'components/customer/Job/store/jobApi';
import {
    getImageDataUrl,
    setIsSavingProduct,
    setIsSavingProductIn3DRP,
    getHasPreview3DLoading,
} from 'components/customer/Preview3DCommon/store/viewerSlice';
import {setCachedPreviewImage} from 'shared/helpers/previewImageCache';
import {cabinetTopsSet} from 'components/customer/Room/store/roomSlice';
import {useProductSaveRedirection} from 'components/customer/RoomPlanner/helper/useProductSaveRedirection';
import {appendProductTo3dPreview} from 'components/customer/RoomPlanner/helpers/appendProductTo3dPreview';
import {get3dTemplate} from 'service/ProductService';
import {useRoomPlannerLeftMenu} from 'components/customer/RoomPlanner/helper/useRoomPlannerLeftMenu';
import {
    removeMeshFromScene,
    updateUserData,
    handlePostSaveRoomPlannerActions,
} from 'hooks/Product/helpers/roomPlannerProducts';
import {ApplianceApi} from 'components/customer/Product/Appliance/store/applianceApi';
import {resolveRangehoodOpeningHeight} from 'components/customer/Product/Appliance/helpers/resolveRangehoodOpeningHeight';
import {
    setBackupProductValues,
    getBackupProductValues,
} from 'components/customer/RoomPlanner/store/roomPlannerSlice';
import {getDisplayMessageForRequestError} from 'shared/helpers/FormatRequestError';
import {useGetCabinetTopsQuery} from 'components/customer/Room/store/roomApi';
import {useRoomPlannerContext} from 'components/customer/RoomPlanner/components/RoomPlannerContext';
import {autoSave} from 'components/customer/RoomPlanner/lib/useAutoSave';
import {normalizeDrawerValues} from 'hooks/Product/helpers/normalizeDrawerValues';
import {createDeferredSubmit} from 'hooks/Product/helpers/deferredSubmit';
import {
    Event,
    genericPubSub,
} from 'components/customer/RoomPlanner/helper/GenericPubSub';
import {setProductType} from 'components/customer/Product/store/productSlice';

export const useProductItem = () => {
    const {
        product: productId,
        cabinetId,
        copy,
        goto,
        height,
        width,
        depth,
        importid,
        validate,
    } = useQueryParams();
    const {jobId, roomId} = useParams();
    const {notify, addMessages, MESSAGE_TYPES} = useNotificationContext();
    const {
        validationData,
        setProduct,
        setInitialValuesRef,
        productDataStore,
        drawerRunnerInfoReference,
        simpleShelvesReference,
        addRecentItem,
        productHasDoor,
        isLoading,
        currentTab,
        setCurrentTab,
        formStructure,
        setFormStructure,
        validateField,
        defaultValuesReference,
        productDefaultsReference,
        materialLoading,
        setMaterialOptions,
        getMaterialOptions,
        setValidationData,
        showDialog,
        setRoomAssistantContent,
    } = useProductContext();
    const {job, updateTotalProductCountManual} = useJobContext();
    const {isMounted} = useComponentMountedHelper();
    const navigate = useNavigate();
    const {setIsMobileMenuVisible} = useAppContext();
    const setMaterials = useMaterialValues();
    const [getProductConfig, {data: productFormFields}] =
        useLazyGetQFPProductStructureQuery();
    const [getProductData] = useLazyGetProductDataQuery();
    const {fetchProductDefaultValues} = useGetProductDefaultQuery();
    const {data: cabinetTops} = useGetCabinetTopsQuery();
    const {getChangedData} = useChangedData({
        productId,
    });
    const {isRoomPlannerPage} = useRoomPlannerLeftMenu();

    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);

    const [initialValues, setInitialValues] = useState({});
    const [formSchema, setFormSchema] = useState({});
    const [tabErrors, setTabErrors] = useState({});
    const [showRoomDefaultDialog, setShowRoomDefaultDialog] = useState(false);
    const loadingDrawerRunners = useAppSelector(selectRunnersLoadingState);
    const faceHeightErrors = useAppSelector(selectFaceHeightErrors);
    const backupProductValues = useAppSelector(getBackupProductValues);
    const [getCabinetDetails] = useLazyGetCabinetDetailsQuery();

    const {data: {products, roomDetails: room} = {}} = useGetRoomProductsQuery(
        {roomId: parseInt(`${roomId}`)},
        {
            skip: typeof roomId === 'undefined',
        }
    );

    const exteriorMaterial = useAppSelector((state) =>
        getMaterial(state, MaterialType.EXTERIOR)
    );

    const carcaseMaterial = useAppSelector((state) =>
        getMaterial(state, MaterialType.CARCASE)
    );

    const exteriorMaterialEdge = useAppSelector((state) =>
        getEdge(state, MaterialType.EXTERIOR)
    );

    const carcaseMaterialEdge = useAppSelector((state) =>
        getEdge(state, MaterialType.CARCASE)
    );

    const selectedDoor = useAppSelector((state) => getDoor(state));

    const validationRulesStore = useRef([]);
    const productValidationStore = useRef({});
    const shelvesStructureStore = useRef({});
    const shelfTypes = useRef({});
    const shelfStyles = useRef({});
    const shelfSectionHeight = useRef({});
    const partitionHeightReferenceStore = useRef();
    const formSubmitted = useRef(false);
    const deferredSubmit = useRef(createDeferredSubmit());
    const cabinetIdRef = useRef(cabinetId);
    // Identifies the current initialLoad, so one still in flight can tell it
    // has been superseded.
    const initialLoadId = useRef(0);
    const invalidateInitialLoad = () => {
        initialLoadId.current += 1;
    };
    const [saveProduct] = useSaveProductMutation();
    const dispatch = useAppDispatch();
    const productQuantity = useRef(0);

    const {userProfile} = useAppContext();

    const {handleRedirection} = useProductSaveRedirection();
    const {cabinetGroupListRef, originalHeightRef, originalDepthRef} =
        useRoomPlannerContext();

    const allow3DView = userProfile?.allow3DView;
    const has3DPreview =
        productDataStore?.current &&
        productDataStore.current?.template_3d &&
        productDataStore.current?.template_3d?.length > 0 &&
        allow3DView;

    const productFieldValidator = (values, formikErrors) => {
        if (validateField.current) {
            try {
                let showEdgeMaterials = true;

                if (Array.isArray(formStructure) && formStructure.length > 0) {
                    const materialFields = getCabinetFields(formStructure);

                    showEdgeMaterials = materialFields?.hasEdgeMaterial;
                }

                const errors = [
                    ...validateProducts(
                        values,
                        validationRulesStore.current,
                        validationData,
                        false,
                        showEdgeMaterials
                    ),
                    ...faceHeightErrors,
                ];

                const updatedErrors = errors.map((error) => ({
                    ...error,
                    ...{onlyField: true},
                }));

                if (errors.length > 0) {
                    const tabErrorValues = getTabErrors(
                        formStructure,
                        updatedErrors
                    );

                    addMessages({
                        type: MESSAGE_TYPES.ERROR,
                        messages: updatedErrors,
                    });
                    setTabErrors(tabErrorValues);
                } else {
                    addMessages({
                        type: MESSAGE_TYPES.ERROR,
                        messages: [],
                    });
                    setTabErrors({});
                }
            } catch (e) {
                addMessages({
                    type: MESSAGE_TYPES.ERROR,
                    messages: [],
                });
                setTabErrors({});
            }
        }
    };

    const recessedRailsDialog = (navigateCallback) => {
        showDialog({
            title: 'Option Selected: Recessed Handle',
            message:
                "You've just saved an item to your job which includes recessed handle cut outs in the cabinet sides. Would you also like to add in rail for this?",
            hideYesButton: true,
            hideNoButton: true,
            buttons: [
                {
                    name: 'Save without adding Rails',
                    show: true,
                    action: () => {
                        navigateCallback();
                    },
                },
                {
                    name: 'View Rail Products',
                    show: true,
                    variant: 'primary-color',
                    action: () => {
                        navigate(
                            `/v2/job/${jobId}/room/${roomId}/product?rails=true`
                        );
                    },
                },
            ],
        });
    };

    // populate redux state with cabinet tops which will be used on product save room assistance
    useEffect(() => {
        if (cabinetTops) {
            dispatch(cabinetTopsSet(cabinetTops));
        }
    }, [cabinetTops]);

    const productFormHandler = async (values) => {
        let showEdgeMaterials = true;

        if (Array.isArray(formStructure) && formStructure.length > 0) {
            const materialFields = getCabinetFields(formStructure);

            showEdgeMaterials = materialFields?.hasEdgeMaterial;
        }

        if (materialLoading || loadingDrawerRunners) {
            setLoading(true);
            formSubmitted.current = true;

            return false;
        }

        addMessages({
            type: MESSAGE_TYPES.ERROR,
            messages: [],
        });
        setTabErrors({});
        const errors = [
            ...validateProducts(
                values,
                validationRulesStore.current,
                validationData,
                false,
                showEdgeMaterials
            ),
            ...faceHeightErrors,
        ];

        if (errors.length == 0) {
            setLoading(true);
            setSaving(true);

            const submittedValues = normalizeDrawerValues(cloneDeep(values));

            const currentDoor = getMaterialOptions?.()?.cabinet_door;
            if (currentDoor && currentDoor.advanced != null) {
                submittedValues.job_cabinet_door_advanced = currentDoor.advanced
                    ? '1'
                    : '0';
            }

            const mappedValues = mapProductData({
                values: submittedValues,
                productDataStore,
                shelvesStructureStore,
                shelfTypes,
                shelfStyles,
            });

            mappedValues.room_id = roomId;
            try {
                if (typeof copy === 'undefined' && cabinetId) {
                    mappedValues.job_cabinet_id = cabinetId;
                }
                let isEmptyData = true;
                let changedData = {};
                if (
                    job.isRoomAssistantEnabled &&
                    userProfile.is_room_assistant_available &&
                    userProfile.room_assistant
                ) {
                    changedData = getChangedData({
                        product: mappedValues,
                        room,
                        materials: {
                            ext: exteriorMaterial,
                            carc: carcaseMaterial,
                            extEdge: showEdgeMaterials
                                ? exteriorMaterialEdge
                                : undefined,
                            carcEdge: showEdgeMaterials
                                ? carcaseMaterialEdge
                                : undefined,
                            door: selectedDoor,
                        },
                    });
                    isEmptyData = changedData.every((change) =>
                        change.items.every(
                            (data) =>
                                Object.keys(data).length == 0 || data.is_saved
                        )
                    );
                    if (changedData && !isEmptyData) {
                        setShowRoomDefaultDialog(true);
                        mappedValues['changed_data'] = changedData;
                        setRoomAssistantContent(mappedValues);
                        // The room default dialog decides whether/when the save
                        // actually happens, so the outcome is reported once that
                        // dialog resolves rather than as a failure now.
                        return deferredSubmit.current.defer();
                    }
                }
                mappedValues['changed_data'] = changedData;
                await productFormSubmitHandler(mappedValues);
                return true;
            } catch (e) {
                genericMessageHandler(notify, e);
                if (isMounted.current) {
                    setLoading(false);
                    setSaving(false);
                }
                formSubmitted.current = false;
                dispatch(setIsSavingProduct(false));
                return false;
            }
        } else {
            const tabErrorValues = getTabErrors(formStructure, errors);
            setTabErrors(tabErrorValues);
            addMessages({type: MESSAGE_TYPES.ERROR, messages: errors});
            setLoading(false);
            setSaving(false);
            formSubmitted.current = false;
            dispatch(setIsSavingProduct(false));
            return false;
        }
    };

    const productFormSubmitHandler = useCallback(
        async (mappedValues, ignoreHistoryDispatch = false) => {
            // this adds 3d model to the room planner, make this conditional
            let cabinetGroups;
            let saveSucceeded = false;

            if (isRoomPlannerPage) {
                dispatch(setIsSavingProductIn3DRP(true));
            }

            try {
                const waitForPreviewImage = async () => {
                    const initialImageDataUrl = getImageDataUrl(
                        store.getState()
                    );
                    const timeoutMs = 12000;
                    const pollMs = 100;
                    const start = Date.now();

                    while (Date.now() - start < timeoutMs) {
                        const state = store.getState();
                        const isLoadingPreview = getHasPreview3DLoading(state);
                        const currentImageDataUrl = getImageDataUrl(state);

                        if (
                            currentImageDataUrl &&
                            currentImageDataUrl !== initialImageDataUrl &&
                            !isLoadingPreview
                        ) {
                            return;
                        }

                        if (
                            currentImageDataUrl &&
                            !isLoadingPreview &&
                            Date.now() - start > 1500
                        ) {
                            return;
                        }

                        await new Promise((resolve) =>
                            setTimeout(resolve, pollMs)
                        );
                    }
                };

                const isUpdate = !(
                    isRoomPlannerPage &&
                    typeof mappedValues.job_cabinet_id === 'undefined'
                );
                const isRoomPlannerUpdate = Boolean(
                    isRoomPlannerPage &&
                    cabinetId &&
                    cabinetGroupListRef?.current
                );
                const backupProduct = backupProductValues
                    ? cloneDeep(backupProductValues)
                    : null;

                cabinetGroups = await appendProductTo3dPreview(
                    mappedValues,
                    productId,
                    room,
                    cabinetGroupListRef,
                    isUpdate
                );

                if (has3DPreview) {
                    await waitForPreviewImage();
                }

                const latestImageDataUrl = has3DPreview
                    ? getImageDataUrl(store.getState())
                    : '';

                const response = await saveProduct({
                    product: {
                        ...mappedValues,
                        ...(has3DPreview
                            ? {imageDataUrl: latestImageDataUrl}
                            : {}),
                    },
                }).unwrap();

                if (has3DPreview && latestImageDataUrl) {
                    setCachedPreviewImage(response.id, latestImageDataUrl);
                }

                updateUserData(cabinetGroups, response.id);

                if (isUpdate) dispatch(setBackupProductValues(null));

                if (isRoomPlannerUpdate) {
                    const cabinet = cabinetGroupListRef?.current.find(
                        (group) => group.userData.values.id == response.id
                    );
                    if (cabinet) autoSave(cabinet, true);
                    if (originalHeightRef?.current)
                        originalHeightRef.current = null;
                    if (originalDepthRef?.current)
                        originalDepthRef.current = null;
                }

                handlePostSaveRoomPlannerActions({
                    isRoomPlannerPage,
                    ignoreHistoryDispatch,
                    cabinetGroupListRef,
                    responseId: response.id,
                    mappedValues,
                    isUpdate,
                    backupProduct,
                    productFormSubmitHandler,
                    dispatch,
                    roomId: Number(roomId),
                    productDataStore,
                    shelvesStructureStore,
                    shelfTypes,
                    shelfStyles,
                });

                addRecentItem(
                    productDataStore.current.type,
                    RECENTLY_ADDED_KEYS.PRODUCT
                );

                genericMessageHandler(
                    notify,
                    {message: 'Product saved'},
                    'success'
                );

                saveSucceeded = true;

                if (isMounted.current) {
                    setLoading(false);
                    setSaving(false);
                }

                if (
                    cabinetId == undefined ||
                    productQuantity.current != mappedValues.cabinet_quantity ||
                    copy
                ) {
                    let increment =
                        mappedValues.cabinet_quantity > productQuantity.current;
                    let quantity = Math.abs(
                        mappedValues.cabinet_quantity - productQuantity.current
                    );

                    if (cabinetId == undefined || copy) {
                        increment = true;
                        quantity = mappedValues.cabinet_quantity;
                    }

                    updateTotalProductCountManual(false, increment, quantity);
                }

                dispatch(invalidateTotalProductCount());
                dispatch(invalidateParticularRoomAndCost(Number(room.id)));
                dispatch(invalidateJob());

                const hasRailProduct = products?.some(
                    (product) => !!product?.is_recessed_rail
                );

                const shouldShowRecessedRailsDialog =
                    mappedValues?.fingerPull_styles > 0 && !hasRailProduct;

                const layoutPath = `/v2/job/${jobId}/room/${roomId}/layout`;
                const kdmaxImportPath = `/v2/job/${jobId}/room/${roomId}/kdmax-import`;
                const dashboardPath = `/v2/job/${jobId}/room/${roomId}/dashboard?pid=${response.id}`;

                const handled = handleRedirection();
                if (handled) {
                    return;
                }

                const handleNavigation = (path) => {
                    if (shouldShowRecessedRailsDialog) {
                        recessedRailsDialog(() => {
                            navigate(path);
                        });
                    } else {
                        navigate(path);
                    }
                };

                if (goto) {
                    switch (goto) {
                        case 'tdld':
                            handleNavigation(layoutPath);
                            break;
                        case 'kdmax-import':
                            // This needs to be moved from this file to somewhere sensible
                            await updateImportedProduct(
                                importid,
                                true,
                                true,
                                response.id
                            );
                            handleNavigation(kdmaxImportPath);
                            break;
                    }
                } else {
                    handleNavigation(dashboardPath);
                }
            } catch (e) {
                removeMeshFromScene(cabinetGroups, cabinetGroupListRef);
                const messageResult = getDisplayMessageForRequestError(e);

                if (messageResult.hasContext) {
                    addMessages({
                        type: MESSAGE_TYPES.ERROR,
                        messages: [
                            {
                                fields: [],
                                message: messageResult.message,
                            },
                        ],
                    });
                } else {
                    const modifiedError = {...e};
                    modifiedError.message = messageResult.message;
                    genericMessageHandler(notify, modifiedError);
                }

                if (isMounted) {
                    setLoading(false);
                    formSubmitted.current = false;
                    dispatch(setIsSavingProduct(false));
                }
            } finally {
                if (isRoomPlannerPage) {
                    dispatch(setIsSavingProductIn3DRP(false));
                }

                deferredSubmit.current.settle(saveSucceeded);
            }
        },
        [
            job,
            validationData,
            productId,
            room,
            products,
            isRoomPlannerPage,
            has3DPreview,
            cabinetId,
            backupProductValues,
        ]
    );

    const initialLoad = async () => {
        const loadId = initialLoadId.current;
        // On a slow connection a load for the previous cabinet can resolve
        // after a switch has started loading the next one (e.g. 3DRP's save
        // then switch). Applying it would put the old cabinet's values into
        // the editor under the new cabinet's id, and from there onto the new
        // cabinet's 3D model.
        const isSuperseded = () => loadId !== initialLoadId.current;

        try {
            const defaultsValues = await fetchProductDefaultValues(
                Number(productId)
            );
            const {data: ogProductConfig} = await getProductConfig(
                {cabinetType: parseInt(productId), qfp: false},
                true
            );
            // Fetch saved product data and product defaults in parallel when
            // editing. Product defaults (without cabinetId) provide the same
            // data used during product creation — room-level default values
            // for gap/top margins. These are needed so the finger pull "None"
            // option can reset handle-affected margins correctly.
            const [productDataResult, productDefaultsResult, template3d] =
                await Promise.all([
                    getProductData(
                        {
                            cabinetId: Number(cabinetId),
                            cabinetType: Number(productId),
                            roomId: Number(roomId),
                        },
                        true
                    ),
                    cabinetId
                        ? getProductData(
                              {
                                  cabinetType: Number(productId),
                                  roomId: Number(roomId),
                              },
                              true
                          )
                        : Promise.resolve({data: null}),
                    get3dTemplate(String(productId)),
                ]);

            if (isSuperseded()) return;

            const {data: productValues} = productDataResult;
            productDefaultsReference.current = cabinetId
                ? productDefaultsResult.data || {}
                : productValues || {};

            const productConfig = cloneDeep(ogProductConfig);
            const productData = cloneDeep(productValues);
            if (productData && template3d) {
                productData.template_3d = template3d;
            }
            const materialFields = getCabinetFields(
                productConfig.originalStructure
            );
            const {validationData, materialOptions, materials} = setMaterials(
                productData,
                materialFields
            );

            const restrictions = getMaterialRestrictionDetails(
                materials,
                materialFields
            );
            const cabinetTopRestriction =
                checkCabinetTopRestriction(productData);

            if (typeof cabinetTopRestriction !== 'undefined') {
                restrictions.push(cabinetTopRestriction);
            }

            let productDetails;

            if (productData.cabinet && productData.cabinet.attributes) {
                productDetails = cloneDeep(productData.cabinet.attributes);
            } else {
                const cabinetDetails = await getCabinetDetails(
                    {
                        cabinetId: parseInt(productId),
                    },
                    true
                ).unwrap();

                if (isSuperseded()) return;

                productDetails = structuredClone(cabinetDetails);
            }

            dispatch(setProductType(productDetails.product_type));

            const kdmaxProduct = importid
                ? await getKDMaxProduct(importid)
                : null;

            if (isSuperseded()) return;

            if (productData && productDetails) {
                if (cabinetId) {
                    productData.cabinet = {
                        attributes: productDetails,
                    };
                }
                productDataStore.current = productData;

                productDetails.productConfig = productConfig.originalStructure;
                validationRulesStore.current = productConfig.validation;
                productHasDoor.current = getDoorInfo({
                    structure: productConfig.originalStructure,
                });

                let initialValues = getInitialValues(
                    productDetails.productConfig
                );
                ({
                    drawerRunnerInfoReference:
                        drawerRunnerInfoReference.current,
                    shelfSectionHeight: shelfSectionHeight.current,
                    partitionHeightReferenceStore:
                        partitionHeightReferenceStore.current,
                    shelvesStructureStore: shelvesStructureStore.current,
                    shelfTypes: shelfTypes.current,
                    shelfStyles: shelfStyles.current,
                } = getRequiredReferences(
                    {
                        structure: productConfig.originalStructure,
                    },
                    initialValues
                ));

                if (!productData.hasOwnProperty('cabinet')) {
                    productDataStore.current.cabinet = {
                        attributes: productDetails,
                    };
                }

                productValidationStore.current = getPanelValidation(
                    productDataStore.current.cabinet.attributes
                );

                setProduct(productDetails);
                setInitialValuesRef({...initialValues}); // setting copy of initial values in context reference as we might need to access original values later

                const shelvesOptions = getOptions(
                    'simple_shelves_amount',
                    productConfig.originalStructure
                );
                // sets correct values from room defaults to correct keys
                initialValues = mapInitialValues({
                    initialValues,
                    productData,
                    shelvesStructureStore,
                    simpleShelvesReference,
                    partitionHeightReferenceStore,
                    cabinetId,
                    shelvesOptions,
                    drawerRunnerInfoReference:
                        drawerRunnerInfoReference.current,
                    productConfig: productDetails.productConfig,
                });
                // Overrides room defaults if form fields values are set
                if (typeof cabinetId == 'undefined') {
                    initialValues = mapDefaults(initialValues, defaultsValues);
                    initialValues = applyAdvancedDoorDrawerFaceTypeDefault(
                        initialValues,
                        materials.door?.is_advanced
                    );
                } else if (materials.door) {
                    initialValues.job_cabinet_door_advanced =
                        productData.job_cabinet_door_advanced;
                }

                if (copy && cabinetId) {
                    initialValues.action = 'copy';
                    initialValues.cabinet_edge_l1 = productData.cabinet_edge_l1;
                    initialValues.cabinet_edge_l2 = productData.cabinet_edge_l2;
                    initialValues.cabinet_edge_w1 = productData.cabinet_edge_w1;
                    initialValues.cabinet_edge_w2 = productData.cabinet_edge_w2;
                    initialValues.cabinet_edge_inner =
                        productData.cabinet_edge_inner;
                }

                if (width && initialValues.hasOwnProperty('cabinet_width')) {
                    initialValues.cabinet_width = width;
                }

                if (height && initialValues.hasOwnProperty('cabinet_height')) {
                    initialValues.cabinet_height = height;
                }

                if (depth && initialValues.hasOwnProperty('cabinet_depth')) {
                    initialValues.cabinet_depth = depth;
                }

                if (
                    depth &&
                    initialValues.hasOwnProperty('cabinet_applied_panel_depth')
                ) {
                    initialValues.cabinet_applied_panel_depth = depth;
                }

                if (goto == 'kdmax-import') {
                    initialValues.cabinet_height = height
                        ? height
                        : initialValues.cabinet_height;
                    initialValues.cabinet_width = width
                        ? width
                        : initialValues.cabinet_width;
                    initialValues.cabinet_depth = depth
                        ? depth
                        : initialValues.cabinet_depth;
                    initialValues.cabinet_applied_panel_depth = depth
                        ? depth
                        : initialValues.cabinet_applied_panel_depth;
                    if (
                        kdmaxProduct.hasOwnProperty('shelves') &&
                        kdmaxProduct.shelves
                    ) {
                        initialValues.shelves = kdmaxProduct.shelves;
                        initialValues.cabinet_simple_shelves = false;
                        initialValues.simple_shelves_amount =
                            kdmaxProduct.shelfCount;
                        simpleShelvesReference.current =
                            kdmaxProduct.shelfCount;
                    }
                    if (
                        kdmaxProduct.hasOwnProperty('drawerDetails') &&
                        kdmaxProduct.drawerDetails.length
                    ) {
                        initialValues.cabinet_drawer_gap =
                            productData.cabinet_drawer_gap;
                        initialValues.cabinet_drawer_bottom =
                            kdmaxProduct.drawerDetails.bottomMargin;
                        initialValues.cabinet_drawer_top =
                            productData.cabinet_drawer_top;
                        initialValues.drawer_face_height =
                            kdmaxProduct.drawerDetails.faceDetails;
                    }
                    if (
                        kdmaxProduct.hasOwnProperty('doorDetails') &&
                        kdmaxProduct.doorDetails
                    ) {
                        initialValues.cabinet_door_top =
                            kdmaxProduct.doorDetails.topMargin;
                        initialValues.cabinet_door_bottom =
                            kdmaxProduct.doorDetails.bottomMargin;
                    }

                    if (initialValues.hasOwnProperty('cabinet_panel_length')) {
                        initialValues.cabinet_panel_length = height;
                    }

                    if (initialValues.hasOwnProperty('cabinet_panel_width')) {
                        initialValues.cabinet_panel_width = width;
                    }

                    if (
                        productData?.cabinet?.attributes?.microwave ||
                        productData?.cabinet?.attributes?.oven
                    ) {
                        let totalDrawerHeight =
                            initialValues.cabinet_total_drawer_height;
                        if (productData?.cabinet?.attributes?.microwave) {
                            totalDrawerHeight =
                                height -
                                initialValues.microwave_opening_height -
                                productData['ext_material']['thickness'] * 2;
                        } else if (productData?.cabinet?.attributes?.oven) {
                            totalDrawerHeight =
                                height - initialValues.oven_opening_height;
                        }

                        const drawers = cloneDeep(initialValues.drawers);
                        for (let i = 0; i < drawers.length; i++) {
                            if (drawers[Number(i)].id == -1) {
                                const faceHeight = getFaceHeight(
                                    drawers,
                                    i,
                                    totalDrawerHeight,
                                    initialValues.cabinet_drawer_bottom,
                                    initialValues.cabinet_drawer_top,
                                    initialValues.cabinet_drawer_gap,
                                    'drawer_face_height',
                                    'drawer_style'
                                );

                                drawers[Number(i)].drawer_face_height =
                                    faceHeight;
                            }
                        }

                        initialValues.drawers = drawers;
                    }

                    initialValues.cabinet_note = kdmaxProduct.name;
                }
                setMaterialOptions(materialOptions);
                setValidationData({
                    type: 'multiple',
                    data: validationData,
                });

                defaultValuesReference.current = initialValues;
                validateField.current = true;

                if (materials) {
                    if (materials?.exterior && materialFields.hasExterior) {
                        initialValues.exterior_colour_name =
                            materials.exterior.name;
                    }

                    if (materials?.carcase && materialFields.hasCarcase) {
                        initialValues.carcase_colour_name =
                            materials.carcase.name;
                    }

                    // set default value for shelves thickness
                    if (cabinetId == undefined) {
                        if (
                            materialFields.hasExterior &&
                            !materialFields.hasCarcase
                        ) {
                            initialValues.simple_shelves_thickness =
                                materials?.exterior.thickness;
                        } else {
                            initialValues.simple_shelves_thickness =
                                materials?.carcase.thickness;
                        }
                    }
                }

                productQuantity.current = initialValues.cabinet_quantity;

                if (productValues?.appliance) {
                    initialValues.appliance = productValues.appliance;
                }

                if (
                    initialValues.appliance?.custom &&
                    typeof initialValues.appliance.height === 'number' &&
                    initialValues.hasOwnProperty('rangehood_opening_height')
                ) {
                    initialValues.rangehood_opening_height =
                        initialValues.appliance.height;
                }

                if (
                    initialValues.appliance &&
                    !initialValues.appliance.custom &&
                    initialValues.hasOwnProperty('rangehood_opening_height')
                ) {
                    try {
                        const catalogue = await dispatch(
                            ApplianceApi.endpoints.fetchAppliances.initiate()
                        ).unwrap();

                        if (isSuperseded()) return;

                        const openingHeight = resolveRangehoodOpeningHeight(
                            initialValues.appliance,
                            catalogue
                        );

                        if (typeof openingHeight === 'number') {
                            initialValues.rangehood_opening_height =
                                openingHeight;
                            initialValues.appliance = {
                                ...initialValues.appliance,
                                height: openingHeight,
                            };
                        }
                    } catch {}
                }

                if (cabinetId && !copy) {
                    initialValues.job_cabinet_id = cabinetId;
                }

                setInitialValues(initialValues);
                setFormSchema(
                    getFormSchema(
                        {
                            structure: productConfig.validStructure,
                            validation: productConfig.validation,
                        },
                        Object.keys(initialValues)
                    )
                );

                setFormStructure(productConfig.originalStructure); // setting form field structure

                if (restrictions.length > 0) {
                    showDialog({
                        title: 'Unavailable Options',
                        message: (
                            <>
                                <p>
                                    Following options are not available. Please
                                    update these options before saving.
                                </p>
                                <ul>
                                    {restrictions.map((restriction) => {
                                        return (
                                            <li key={restriction}>
                                                {parseHtmlString(restriction)}
                                            </li>
                                        );
                                    })}
                                </ul>
                            </>
                        ),
                        hideYesButton: true,
                        hideNoButton: true,
                        buttons: [
                            {
                                title: 'Ok',
                                name: 'Ok',
                                show: true,
                            },
                        ],
                    });
                }
            } else {
                genericMessageHandler(notify, {
                    message:
                        'Could not fetch product details, please log out and back in then try again later',
                });
            }

            setLoading(false);
        } catch (errors) {
            if (isSuperseded()) return;

            setLoading(false);
            genericMessageHandler(notify, errors);
        }
    };

    const submitFormManual = () => {
        const submitButton = document.querySelector('#product-submit-button');

        if (submitButton && !submitButton.disabled) {
            formSubmitted.current = true;
            submitButton.click();
        }
    };

    useEffect(() => {
        if (productFormFields) {
            const newSchema = getFormSchema(
                {
                    structure: productFormFields.validStructure,
                    validation: productFormFields.validation,
                },
                Object.keys(initialValues)
            );
            setFormSchema(newSchema);
        }
    }, [initialValues]);

    useLayoutEffect(() => {
        if (validate && !isLoading && !formSubmitted.current) {
            submitFormManual();
        }
        setIsMobileMenuVisible(false);
    }, [isLoading]);

    useEffect(() => {
        const clearProductEditor = () => {
            setInitialValues({});
            setLoading(true);
            setSaving(false);
            setTabErrors({});
        };

        genericPubSub.subscribe(
            Event.ROOM_PLANNER_CLEAR_PRODUCT_EDITOR,
            clearProductEditor
        );

        return () => {
            genericPubSub.unsubscribe(
                Event.ROOM_PLANNER_CLEAR_PRODUCT_EDITOR,
                clearProductEditor
            );
        };
    }, []);

    useEffect(() => {
        if (cabinetIdRef.current != cabinetId) {
            cabinetIdRef.current = cabinetId;
            validationRulesStore.current = [];
            productValidationStore.current = {};
            shelvesStructureStore.current = {};
            shelfTypes.current = {};
            shelfStyles.current = {};
            shelfSectionHeight.current = {};
            partitionHeightReferenceStore.current = undefined;
            formSubmitted.current = false;

            setFormSchema({});
            setTabErrors({});
            setInitialValues({});
        }

        setLoading(true);

        initialLoad();

        return () => {
            invalidateInitialLoad();
            addMessages({});
            setTabErrors({});
        };
    }, [productId, cabinetId]);

    useEffect(() => {
        if (!materialLoading && formSubmitted.current) {
            submitFormManual();
        }
    }, [materialLoading]);

    // this effect will submit the form in case drawer runner is still loading
    useEffect(() => {
        if (!loadingDrawerRunners && formSubmitted.current) {
            submitFormManual();
        }
    }, [loadingDrawerRunners]);

    const cancelDeferredSubmit = useCallback(() => {
        deferredSubmit.current.settle(false);
    }, [deferredSubmit]);

    // Nothing can report the outcome once the editor is gone, so release anyone
    // still waiting rather than leaving them hanging forever.
    useEffect(() => cancelDeferredSubmit, [cancelDeferredSubmit]);

    const hasMaterialEdge = useMemo(() => {
        if (!Array.isArray(formStructure) || formStructure.length === 0)
            return true;

        return getCabinetFields(formStructure)?.hasEdgeMaterial;
    }, [formStructure]);

    const materialTabIndex = useMemo(() => {
        if (!Array.isArray(formStructure)) return 1;

        const index = formStructure
            .filter((fs) => fs?.layout != 'sidebar')
            .map((fs) => fs.name.toLowerCase())
            .indexOf('materials');

        return index || 1;
    }, [formStructure]);

    // Calculated again so other validation doesn't overwrite it
    const materialTabErrorCount = useMemo(() => {
        if (!validationData) return 0;

        return checkMaterialRestrictions(validationData, hasMaterialEdge)
            .length;
    }, [validationData, hasMaterialEdge]);

    const mergedTabErrors = useMemo(() => {
        const updatedTabErrors = {...tabErrors};

        if (materialTabErrorCount === 0) {
            // eslint-disable-next-line security/detect-object-injection -- materialTabIndex is a safe numeric tab index
            delete updatedTabErrors[materialTabIndex];
        } else {
            // eslint-disable-next-line security/detect-object-injection -- materialTabIndex is a safe numeric tab index
            updatedTabErrors[materialTabIndex] = materialTabErrorCount;
        }

        return updatedTabErrors;
    }, [tabErrors, materialTabErrorCount, materialTabIndex]);

    return {
        showRoomDefaultDialog,
        setShowRoomDefaultDialog,
        currentTab,
        setCurrentTab,
        productId,
        initialValues,
        formSchema,
        formStructure,
        loading,
        setLoading,
        productFormHandler,
        productFieldValidator,
        tabErrors: mergedTabErrors,
        setTabErrors,
        productFormSubmitHandler,
        cancelDeferredSubmit,
        saving,
    };
};
